import { CheckRounded, RemoveRounded } from "@mui/icons-material";
import { Box, Stack, SxProps, ToggleButton, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { StatusGroupSelectionState } from "src/constants";

interface Props<T> {
  label: string;
  selectedStatusIds: T[];
  childStatusIds: T[];
  onClick: (selectedStatuses: T[]) => void;
  sx?: SxProps;
}

const StatusGroupToggleButton = <T extends string | number>(props: Props<T>) => {
  const [selectedState, setSelectedState] = useState(StatusGroupSelectionState.None);
  const theme = useTheme();

  useEffect(() => {
    function getSelectedState() {
      const selectedStatusCount = (props.selectedStatusIds ?? []).filter((x) =>
        props.childStatusIds.includes(x),
      ).length;

      if (selectedStatusCount === props.childStatusIds.length) {
        return StatusGroupSelectionState.All;
      } else if (selectedStatusCount > 0) {
        return StatusGroupSelectionState.Partial;
      } else {
        return StatusGroupSelectionState.None;
      }
    }

    setSelectedState(getSelectedState());
  }, [props.selectedStatusIds, props.childStatusIds]);

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();

    const selectedStatuses = new Set(props.selectedStatusIds ?? []);
    const childStatuses = new Set(props.childStatusIds);
    let newSelectedStatuses: T[] = [];

    if (selectedStatuses.isSupersetOf(childStatuses)) {
      // If all active statuses are selected, toggle them off
      newSelectedStatuses = [...selectedStatuses.difference(childStatuses)];
    } else {
      // Else add all active statuses to the current selection
      newSelectedStatuses = [...new Set([...selectedStatuses, ...childStatuses])];
    }

    props.onClick(newSelectedStatuses);
  }

  return (
    <ToggleButton value="" onClick={handleClick} sx={{ ...props.sx }}>
      <Stack
        direction="row"
        sx={{
          color: selectedState !== StatusGroupSelectionState.None ? theme.palette.primary.main : undefined,
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box>
          <Typography>{props.label}</Typography>
        </Box>
        <Box>
          {selectedState === StatusGroupSelectionState.All && <CheckRounded />}
          {selectedState === StatusGroupSelectionState.Partial && <RemoveRounded />}
        </Box>
      </Stack>
    </ToggleButton>
  );
};

export { StatusGroupToggleButton };
